/*
 * theme.ts — typed helpers for referencing the CSS tokens defined in theme.css.
 *
 * Use token("color-gold") instead of writing "var(--color-gold)" or, worse, the
 * raw hex literal. Keeps one spelling and lets TS autocomplete the token names.
 * Colors, spacing and durations all live in theme.css; layout numbers (design
 * px the Stage scales) live in layout.ts.
 */

export type ColorToken =
  | 'color-bg'
  | 'color-surface'
  | 'color-surface-2'
  | 'color-border'
  | 'color-text'
  | 'color-muted'
  | 'color-gold'
  | 'color-gold-deep'
  | 'color-on-gold'
  | 'color-accent'
  | 'color-win'
  | 'color-lose'
  | 'color-team-0'
  | 'color-team-1'
  | 'color-team-2'
  | 'color-team-3'
  | 'color-felt-deep'
  | 'color-avatar-top'
  | 'color-avatar-bottom'
  | 'color-avatar-initial';

export type SpaceToken =
  'space-1' | 'space-2' | 'space-3' | 'space-4' | 'space-5' | 'space-6' | 'space-7';

export type DurationToken = 'dur-fast' | 'dur-base' | 'dur-slow' | 'dur-slower' | 'dur-flash';

export type RadiusToken = 'radius-sm' | 'radius-md' | 'radius-lg' | 'radius-pill';

export type ZToken = 'z-table' | 'z-seats' | 'z-trick' | 'z-hud' | 'z-modal';

export type Token = ColorToken | SpaceToken | DurationToken | RadiusToken | ZToken | string;

/** Reference a CSS custom property as a `var(--name)` string. */
export const token = (name: Token): string => `var(--${name})`;

/**
 * A z-index token as a value assignable to React's `style.zIndex`. Inline
 * `z-index: var(--z-hud)` is valid CSS; the cast just satisfies the number type.
 */
export const zVar = (name: ZToken): number => `var(--${name})` as unknown as number;

/** The team color token for a given team index (0..3), wrapping around. */
export const teamColor = (teamIndex: number): string =>
  token(`color-team-${teamIndex % 4}` as ColorToken);
